<template>
  <div class="relative w-full">
    <Input
      :model-value="displayValue"
      :placeholder="placeholder"
      class="w-full cursor-pointer"
      variant="ghost"
      readonly
      @click="showPicker = true"
    />
    <button
      v-if="hasValue"
      class="absolute right-2 top-1/2 -translate-y-1/2 text-obsidian-400 hover:text-obsidian-600"
      @click.stop="clearRange"
    >
      <XIcon class="h-4 w-4" />
    </button>
    <div
      v-if="showPicker"
      ref="pickerContainer"
      class="absolute z-10 mt-1 rounded-md border border-obsidian-200 bg-white shadow-lg"
    >
      <Datepicker
        v-model="internalDates"
        range
        inline
        week-numbers
        :partial-range="false"
        :month-change-on-scroll="false"
        :clearable="false"
        :start-date="startDateForPicker"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import Datepicker from '@/components/Datepicker.vue';
import Input from '@/components/Input.vue';
import { XIcon } from 'lucide-vue-next';
import { getDateFormat } from '@/hooks/use-date-format';
import { formatDate } from '@/hooks/use-formatted-date';

const props = withDefaults(
  defineProps<{
    startDate?: Date | null;
    endDate?: Date | null;
    placeholder?: string;
    autoOpen?: boolean;
  }>(),
  {
    startDate: null,
    endDate: null,
    placeholder: '',
    autoOpen: false,
  },
);

const emit = defineEmits<{
  (e: 'update:startDate', value: Date | null): void;
  (e: 'update:endDate', value: Date | null): void;
  (e: 'change', value: { startDate: Date | null; endDate: Date | null }): void;
}>();

const showPicker = ref(props.autoOpen);
const pickerContainer = ref<HTMLElement | null>(null);

// Internal dates for the picker - use current month as starting point when no dates selected
const internalDates = ref<[Date, Date] | null>(
  props.startDate && props.endDate ? [props.startDate, props.endDate] : null,
);

// Start date for picker calendar view - defaults to today if no dates selected
const startDateForPicker = computed(() => {
  if (props.startDate) return props.startDate;
  return new Date();
});

// Watch for external prop changes
watch(
  () => [props.startDate, props.endDate],
  ([start, end]) => {
    if (start && end) {
      internalDates.value = [start, end];
    } else {
      internalDates.value = null;
    }
  },
);

// Watch for internal date changes and emit when both dates are selected
watch(internalDates, (value) => {
  if (value && value.length === 2 && value[0] && value[1]) {
    emit('update:startDate', value[0]);
    emit('update:endDate', value[1]);
    emit('change', { startDate: value[0], endDate: value[1] });
    showPicker.value = false;
  }
});

onClickOutside(pickerContainer, () => {
  showPicker.value = false;
});

const hasValue = computed(() => props.startDate || props.endDate);

const displayValue = computed(() => {
  if (!props.startDate && !props.endDate) return '';
  const format = getDateFormat();
  const startStr = props.startDate ? formatDate(props.startDate, format) : '';
  const endStr = props.endDate ? formatDate(props.endDate, format) : '';
  if (startStr && endStr) return `${startStr} - ${endStr}`;
  if (startStr) return `${startStr} - ...`;
  return '';
});

const clearRange = () => {
  internalDates.value = null;
  emit('update:startDate', null);
  emit('update:endDate', null);
  emit('change', { startDate: null, endDate: null });
};
</script>
